import React, { useState, useEffect } from 'react';
import { X, Megaphone, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const STYLES = {
  info: { bg: 'bg-[#0A0A0A]', text: 'text-white', link: 'text-[#FCA5A5] hover:text-white', icon: Info },
  success: { bg: 'bg-green-700', text: 'text-white', link: 'text-green-100 hover:text-white underline', icon: CheckCircle2 },
  warning: { bg: 'bg-amber-600', text: 'text-white', link: 'text-amber-100 hover:text-white underline', icon: AlertTriangle },
  urgent: { bg: 'bg-[#BA1826]', text: 'text-white', link: 'text-white/90 hover:text-white underline', icon: Megaphone },
};

const AnnouncementBanner = () => {
  const { data, loading } = useSiteContent('site_announcement', { enabled: false });
  const [dismissed, setDismissed] = useState(false);

  // Persist dismissal per-message (so a new message re-appears)
  useEffect(() => {
    if (!data?.message) return;
    try {
      const key = 'nhf_announcement_dismissed';
      const stored = localStorage.getItem(key);
      if (stored === data.message) setDismissed(true);
    } catch {}
  }, [data?.message]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem('nhf_announcement_dismissed', data.message || '');
    } catch {}
  };

  if (loading || !data?.enabled || !data?.message || dismissed) return null;

  // Scheduling: show only if show_after is null/past AND hide_after is null/future
  const todayStr = new Date().toISOString().slice(0, 10);
  if (data.show_after && todayStr < data.show_after) return null;
  if (data.hide_after && todayStr >= data.hide_after) return null;

  const styleKey = data.style && STYLES[data.style] ? data.style : 'info';
  const S = STYLES[styleKey];
  const Icon = S.icon;

  return (
    <div
      className={`${S.bg} ${S.text} w-full`}
      data-testid="announcement-banner"
      role="region"
      aria-label="Site announcement"
    >
      <div className="max-w-[1100px] mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon className="w-4 h-4 flex-shrink-0" />
          <p className="text-xs sm:text-sm leading-snug truncate sm:whitespace-normal" data-testid="announcement-message">
            {data.message}
            {data.link_url && data.link_label && (
              <>
                {' '}
                <a
                  href={data.link_url}
                  className={`font-semibold underline ${S.link} ml-1`}
                  data-testid="announcement-link"
                >
                  {data.link_label} →
                </a>
              </>
            )}
          </p>
        </div>
        {data.dismissible !== false && (
          <button
            onClick={handleDismiss}
            aria-label="Dismiss announcement"
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            data-testid="announcement-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AnnouncementBanner;
